import { ArrowRightIcon } from '@approvalmax/ui';
import { Form, Grid, NumberField } from '@approvalmax/ui/src/components';
import { mathService } from '@approvalmax/utils';
import { memo, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { FixedSide } from 'shared/data/wallets/v1';
import { z } from 'zod';

import { schema } from '../ConvertCurrencyPopupContent/ConvertCurrencyPopupContent.schema';
import { messages } from './ConvertAmountInputs.messages';
import { ArrowContainer } from './ConvertAmountInputs.styles';
import { ConvertAmountInputsProps } from './ConvertAmountInputs.types';

export const ConvertAmountInputs = memo<ConvertAmountInputsProps>((props) => {
    const { rate: rateOriginal, isRateInversed } = props;

    const form = useFormContext<z.infer<typeof schema>>();

    const [fromCurrency, toCurrency] = form.watch(['fromCurrency', 'toCurrency']);

    const rate = useMemo(() => {
        if (!rateOriginal) {
            return null;
        }

        return isRateInversed ? mathService.round(mathService.divide(1, rateOriginal), 6) : rateOriginal;
    }, [rateOriginal, isRateInversed]);

    useEffect(() => {
        const { fixedSide, fromAmount, toAmount } = form.getValues();

        if (fixedSide === FixedSide.sell) {
            const newToAmount =
                typeof fromAmount === 'number' && rate
                    ? mathService.round(mathService.multiply(fromAmount, rate), 2)
                    : null;

            form.setValue('toAmount', newToAmount);
        } else {
            const newFromAmount =
                typeof toAmount === 'number' && rate
                    ? mathService.round(mathService.divide(toAmount ?? 0, rate), 2)
                    : null;

            form.setValue('fromAmount', newFromAmount);
        }
    }, [rate, form]);

    form.register('fromAmount', {
        onChange: (event) => {
            const value = event.target.value;

            form.setValue('fixedSide', FixedSide.sell);

            if (rate) {
                const newToAmount = value !== null ? mathService.round(mathService.multiply(value, rate), 2) : null;

                form.setValue('toAmount', newToAmount);
            }
        },
        deps: ['toAmount'],
    });

    form.register('toAmount', {
        onChange: (event) => {
            const value = event.target.value;

            form.setValue('fixedSide', FixedSide.buy);

            if (rate) {
                const newFromAmount =
                    value !== null ? mathService.round(mathService.divide(value ?? 0, rate), 2) : null;

                form.setValue('fromAmount', newFromAmount);
            }
        },
        deps: ['fromAmount'],
    });

    return (
        <Form.Part>
            <Grid gridTemplateColumns='1fr max-content 1fr' alignItems='flex-start' gap={8}>
                <NumberField
                    name='fromAmount'
                    // Need to fallback to space here to avoid layout shifting
                    label={fromCurrency || ' '}
                    placeholder={messages.enterValue}
                    size='medium'
                    allowFloat
                    changeOnBlur={false}
                    precision={2}
                    disabled={!fromCurrency}
                />

                <ArrowContainer>
                    <ArrowRightIcon size={16} />
                </ArrowContainer>

                <NumberField
                    name='toAmount'
                    // Need to fallback to space here to avoid layout shifting
                    label={toCurrency || ' '}
                    placeholder={messages.enterValue}
                    size='medium'
                    allowFloat
                    changeOnBlur={false}
                    precision={2}
                    disabled={!toCurrency}
                />
            </Grid>
        </Form.Part>
    );
});

ConvertAmountInputs.displayName = 'ConvertAmountInputs';
